import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BookOpen, Clock, Star, Search, ChevronLeft, Target, Users, Award, TrendingUp, Zap, Home } from 'lucide-react'

// Enhanced Learning Paths Data for the list page
const learningPaths = [
  {
    id: 1,
    title: "AI & Machine Learning Mastery",
    description: "From fundamentals to advanced AI applications and real-world implementations. Build and deploy intelligent systems.",
    category: "Technology & AI",
    courseCount: 5,
    totalDuration: "14 weeks",
    rating: 4.9,
    color: "from-blue-500 to-purple-600",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=250&fit=crop",
    studentCount: 2500,
    level: "Beginner to Advanced",
    certificate: true
  },
  {
    id: 2,
    title: "Web Development Bootcamp",
    description: "Build modern web applications with React and full-stack technologies from scratch. Master the complete development lifecycle.",
    category: "Technology & AI",
    courseCount: 4,
    totalDuration: "12 weeks",
    rating: 4.8,
    color: "from-green-500 to-teal-600",
    image: "https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400&h=250&fit=crop",
    studentCount: 3200,
    level: "Beginner to Intermediate",
    certificate: true
  },
  {
    id: 3,
    title: "Startup Entrepreneurship",
    description: "Launch and scale your business idea with proven strategies and frameworks. Learn from successful founders.",
    category: "Business & Entrepreneurship", 
    courseCount: 4,
    totalDuration: "10 weeks",
    rating: 4.7,
    color: "from-orange-500 to-red-600",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=400&h=250&fit=crop",
    studentCount: 1800,
    level: "All Levels",
    certificate: true
  },
  {
    id: 4,
    title: "Digital Creative Suite",
    description: "Master design, music, and content creation with industry-standard tools. Build your creative portfolio.",
    category: "Creative Arts",
    courseCount: 4,
    totalDuration: "10 weeks",
    rating: 4.8,
    color: "from-purple-500 to-pink-600",
    image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400&h=250&fit=crop",
    studentCount: 2100,
    level: "Beginner to Intermediate",
    certificate: true
  },
  {
    id: 5,
    title: "Personal Productivity System",
    description: "Build habits and systems for success in personal and professional life. Master time management and goal achievement.",
    category: "Personal Development",
    courseCount: 3,
    totalDuration: "8 weeks",
    rating: 4.6,
    color: "from-indigo-500 to-blue-600",
    image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=400&h=250&fit=crop",
    studentCount: 2900,
    level: "All Levels",
    certificate: true
  },
  {
    id: 6,
    title: "Data Science Fundamentals",
    description: "Learn data analysis, visualization, and machine learning from scratch. Work with real-world datasets.",
    category: "Technology & AI",
    courseCount: 5,
    totalDuration: "16 weeks",
    rating: 4.9,
    color: "from-teal-500 to-cyan-600",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop",
    studentCount: 3400,
    level: "Beginner to Intermediate",
    certificate: true
  },
  {
    id: 7,
    title: "Digital Marketing Mastery",
    description: "Master online marketing strategies across all major platforms. Drive growth and engagement.",
    category: "Business & Entrepreneurship",
    courseCount: 4,
    totalDuration: "9 weeks",
    rating: 4.7,
    color: "from-yellow-500 to-orange-600",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop",
    studentCount: 2700,
    level: "All Levels",
    certificate: true
  },
  {
    id: 8,
    title: "Financial Literacy & Investment",
    description: "Build wealth and financial security through smart investment strategies. Plan for your financial future.",
    category: "Personal Development",
    courseCount: 4,
    totalDuration: "11 weeks",
    rating: 4.8,
    color: "from-emerald-500 to-green-600",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=250&fit=crop",
    studentCount: 2300,
    level: "Beginner to Advanced",
    certificate: true
  },
  {
    id: 9,
    title: "Health & Wellness Science",
    description: "Evidence-based approaches to physical and mental wellbeing. Build sustainable healthy habits.",
    category: "Health & Wellness",
    courseCount: 3,
    totalDuration: "7 weeks",
    rating: 4.7,
    color: "from-green-500 to-emerald-600",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop",
    studentCount: 1900,
    level: "All Levels",
    certificate: true
  },
  {
    id: 10,
    title: "Mobile App Development",
    description: "Create cross-platform mobile applications with React Native. Build and publish your own apps.",
    category: "Technology & AI",
    courseCount: 4,
    totalDuration: "13 weeks",
    rating: 4.8,
    color: "from-purple-500 to-indigo-600",
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=250&fit=crop",
    studentCount: 2800,
    level: "Intermediate",
    certificate: true
  }
];

function LearningPathsList() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  
  const categories = ['All', 'Technology & AI', 'Business & Entrepreneurship', 'Creative Arts', 
                     'Personal Development', 'Health & Wellness', 'Science & Innovation']

  const filteredPaths = learningPaths.filter(path => {
    const matchesSearch = path.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         path.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || path.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  // Stats for the hero section
  const pathsStats = {
    totalCourses: learningPaths.reduce((sum, path) => sum + path.courseCount, 0),
    totalDuration: "100+ weeks",
    totalPaths: learningPaths.length,
    totalStudents: learningPaths.reduce((sum, path) => sum + path.studentCount, 0).toLocaleString(),
    averageRating: 4.8
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Enhanced Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          {/* Back Button */}
          <button
            onClick={() => navigate('/courses')}
            className="flex items-center space-x-2 text-primary hover:text-primary/80 mb-6"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Learning Hub</span>
          </button>

          {/* Hero Card */}
          <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-8 md:p-12 text-white mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Learning Paths Library
            </h1>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl">
              Follow structured paths to master complex skills. Each learning path combines multiple courses, projects, and assessments for comprehensive skill development.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-8">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Target className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{pathsStats.totalPaths}</p>
                <p className="text-sm text-purple-100">Paths</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <BookOpen className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{pathsStats.totalCourses}</p>
                <p className="text-sm text-purple-100">Courses</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{pathsStats.totalDuration}</p>
                <p className="text-sm text-purple-100">Content</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Users className="w-6 h-6" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{pathsStats.totalStudents}</p>
                <p className="text-sm text-purple-100">Students</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="flex items-center justify-center mb-2">
                  <Star className="w-6 h-6 fill-current" />
                </div>
                <p className="text-2xl md:text-3xl font-bold">{pathsStats.averageRating}</p>
                <p className="text-sm text-purple-100">Avg Rating</p>
              </div>
            </div>

            {/* Key Features */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Target className="w-4 h-4" />
                <span className="font-medium">Structured Learning</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Award className="w-4 h-4" />
                <span className="font-medium">Certificates Available</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <TrendingUp className="w-4 h-4" />
                <span className="font-medium">Progressive Difficulty</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Zap className="w-4 h-4" />
                <span className="font-medium">Hands-on Projects</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/30 px-4 py-2 rounded-full">
                <Users className="w-4 h-4" />
                <span className="font-medium">Community Support</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Browse All Learning Paths</h2>
          <p className="text-gray-600">Filter and search through our complete collection of structured learning paths</p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Paths
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by title, description, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="w-full md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent appearance-none"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {/* Dropdown arrow */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Results Count and Clear Filters */}
          <div className="flex items-center justify-between pt-4 mt-4 border-t">
            <p className="text-gray-600 text-sm">
              Showing <span className="font-semibold">{filteredPaths.length}</span> learning paths
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </p>
            
            {/* Clear Filters Button */}
            {(searchTerm || selectedCategory !== 'All') && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('All')
                }}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Learning Paths Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPaths.map((path) => (
            <motion.div
              key={path.id}
              whileHover={{ y: -4 }}
              className={`bg-gradient-to-br ${path.color} rounded-xl p-6 text-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative h-full flex flex-col`}
              onClick={() => navigate(`/learning-path/${path.id}`)}
            >
              {/* Path Image */}
              <div className="mb-4 h-40 overflow-hidden rounded-lg">
                <img 
                  src={path.image} 
                  alt={path.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <Target className="w-5 h-5 flex-shrink-0" />
                  <h3 className="text-lg font-bold line-clamp-1">{path.title}</h3>
                </div>
                
                <p className="text-white/90 text-sm line-clamp-2 mb-4">
                  {path.description}
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-xs text-white/80">
                    <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{path.courseCount} courses</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-white/80">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{path.totalDuration}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-white/80">
                    <Star className="w-3.5 h-3.5 flex-shrink-0 fill-current" />
                    <span>{path.rating} • {path.studentCount.toLocaleString()} students</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-auto pt-4">
                <button 
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/learning-path/${path.id}`)
                  }}
                  className="w-full flex items-center justify-center space-x-2 py-2.5 bg-white/30 rounded-lg hover:bg-white/40 transition-colors duration-200 text-sm font-medium"
                >
                  <span>Explore Path</span>
                  <ChevronLeft className="w-4 h-4 rotate-180" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredPaths.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No learning paths found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? `No results for "${searchTerm}"` : 'Try adjusting your search criteria'}
            </p>
            <button
              onClick={() => {
                setSearchTerm('')
                setSelectedCategory('All')
              }}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default LearningPathsList